import React from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getOrder, EshopOrder } from '../services/eshopApi';
import { Box, Heading, Text, VStack, HStack, Button, Spinner, Divider, Link } from '@chakra-ui/react';

const formatPrice = (cents: number, currency: string) => {
  const value = cents / 100;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(value);
};

const OrderSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderIdParam = searchParams.get('order_id');
  const orderId = orderIdParam ? Number(orderIdParam) : NaN;
  const enabled = !Number.isNaN(orderId) && orderId > 0;

  const { data, isLoading, isError } = useQuery<EshopOrder>(
    ['eshop-order', orderId],
    () => getOrder(orderId),
    { enabled }
  );

  if (!enabled) {
    return (
      <Box>
        <Heading size="lg" mb={4}>Děkujeme za vaši objednávku</Heading>
        <Text mb={4}>Platba proběhla úspěšně. Shrnutí objednávky momentálně nemáme k dispozici.</Text>
        <Button as={RouterLink} to="/" colorScheme="blue">
          Zpět do obchodu
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="lg" />
        <Text mt={4}>Načítám vaši objednávku…</Text>
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box>
        <Heading size="lg" mb={4}>Děkujeme za vaši objednávku</Heading>
        <Text mb={4}>Platba proběhla, ale nepodařilo se načíst detail objednávky.</Text>
        <Text mb={4}>Zkontrolujte prosím potvrzovací email nebo se vraťte zpět do obchodu.</Text>
        <Button as={RouterLink} to="/" colorScheme="blue">
          Zpět do obchodu
        </Button>
      </Box>
    );
  }

  const total = data.total_amount_cents || 0;

  return (
    <Box>
      <Heading size="lg" mb={4}>Děkujeme za vaši objednávku</Heading>
      <VStack align="stretch" spacing={3} mb={6}>
        <Text>
          Číslo objednávky: <strong>{data.order_number}</strong>
        </Text>
        <Text>
          Stav: <strong>{data.status}</strong>
        </Text>
        <Text>
          Celková částka: <strong>{formatPrice(total, data.currency || 'CZK')}</strong>
        </Text>
      </VStack>

      {data.items && data.items.length > 0 && (
        <Box mb={6}>
          <Heading size="md" mb={3}>Přehled položek</Heading>
          <VStack align="stretch" spacing={2}>
            {data.items.map((item) => (
              <HStack key={item.id} justify="space-between">
                <Text>{item.name} × {item.quantity}</Text>
                <Text>{formatPrice(item.unit_price_cents * item.quantity, item.currency || data.currency || 'CZK')}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      )}

      <Divider mb={4} />

      <HStack spacing={4}>
        <Button as={RouterLink} to="/" colorScheme="blue">
          Zpět do obchodu
        </Button>
        <Link as={RouterLink} to="/cart" fontSize="sm" color="blue.500">
          Zobrazit košík
        </Link>
      </HStack>
    </Box>
  );
};

export default OrderSuccessPage;
